import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HiOutlineFunnel } from 'react-icons/hi2';
import { FiEdit, FiCopy } from 'react-icons/fi';
import {
  TicketListItem,
  TicketStatus,
  TICKET_STATUS_LABELS,
  TICKET_PRIORITY_LABELS,
} from '../../../types/ticketInterfaces';
import { SlaIndicator } from '../../../shared/ui/sla-indicator';
import { useAppDispatch } from '../../../app/store/store';
import { useHdPermissions } from '@shared/lib/ability';
import {
  solveTicket,
  closeTicket,
  updateTicket,
} from '../../../entities/ticket/model';
import {
  useTicketStatusDict,
  TicketStatusDict,
} from '../../../shared/lib/ticket-statuses';

/* ══════════════════════════════════════════════
   Props
   ══════════════════════════════════════════════ */

interface TicketsTableProps {
  tickets: TicketListItem[];
  totalCount: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  isLoading: boolean;
  /** Коллбэк для колонок-фильтров — (fieldName, value) */
  onColumnFilter?: (field: string, value: string) => void;
}

/* ══════════════════════════════════════════════
   Helpers
   ══════════════════════════════════════════════ */

const fmtDate = (iso: string) => {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
  } catch {
    return iso;
  }
};

const cut = (s: string | undefined, n = 40) =>
  !s ? '' : s.length > n ? s.slice(0, n) + '…' : s;

const personName = (u: { first_name?: string; last_name?: string } | null | undefined) => {
  if (!u) return '—';
  const last = u.last_name ?? '';
  const first = (u.first_name ?? '').charAt(0);
  return first ? `${last} ${first}.` : last || '—';
};

const companyLabel = (c: { name?: string } | null | undefined) =>
  c?.name ? cut(c.name, 22) : '—';

/* ══════════════════════════════════════════════
   Dropdown для фильтрации по колонке
   ══════════════════════════════════════════════ */

interface ColumnFilterDropdownProps {
  field: string;
  options: { value: string; label: string }[];
  onSelect: (field: string, value: string) => void;
  onClose: () => void;
}

function ColumnFilterDropdown({ field, options, onSelect, onClose }: ColumnFilterDropdownProps) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [onClose]);

  return (
    <div
      ref={ref}
      className="absolute top-full left-0 mt-1 z-50 bg-white border border-[#ECEDF0] rounded-lg shadow-lg py-1 min-w-[160px]"
    >
      <button
        onClick={() => { onSelect(field, ''); onClose(); }}
        className="w-full text-left px-3 py-1.5 text-xs text-[#8B8E99] hover:bg-[#F4F4F6]"
      >
        Все
      </button>
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => { onSelect(field, o.value); onClose(); }}
          className="w-full text-left px-3 py-1.5 text-xs text-[#242426] hover:bg-[#F4F4F6]"
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════
   Заголовок с фильтром
   ══════════════════════════════════════════════ */

interface ThFilterProps {
  children: React.ReactNode;
  field: string;
  options: { value: string; label: string }[];
  onSelect: (field: string, value: string) => void;
}

function ThFilter({ children, field, options, onSelect }: ThFilterProps) {
  const [open, setOpen] = useState(false);

  return (
    <th className="text-left text-xs font-semibold text-[#8B8E99] uppercase tracking-wide py-3 px-4 whitespace-nowrap relative">
      <span
        className="inline-flex items-center gap-1.5 cursor-pointer select-none"
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
      >
        <HiOutlineFunnel className="w-3.5 h-3.5 text-[#B0B3BE] hover:text-[#E53935] transition-colors" />
        {children}
      </span>
      {open && (
        <ColumnFilterDropdown
          field={field}
          options={options}
          onSelect={onSelect}
          onClose={() => setOpen(false)}
        />
      )}
    </th>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-xs font-semibold text-[#8B8E99] uppercase tracking-wide py-3 px-4 whitespace-nowrap">
      {children}
    </th>
  );
}

/* ══════════════════════════════════════════════
   Бейдж статуса
   ══════════════════════════════════════════════ */

function StatusBadge({
  status,
  dict,
}: {
  status: TicketStatus;
  dict: TicketStatusDict;
}) {
  const label = dict.labelFor(status);
  const color = dict.colorFor(status);
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-semibold"
      style={{ color, backgroundColor: `${color}14` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

/* ══════════════════════════════════════════════
   Инлайн-смена статуса прямо из списка
   Используем штатные экшены solve/close (а не сырой PATCH status),
   чтобы не обойти бизнес-логику бэка: mark_solved выставляет solved_at,
   шлёт уведомления и планирует автозакрытие. Видно только HD-staff;
   «Закрыть» — только admin/lead (бэк: IsAdminOrLeadOperator).
   ══════════════════════════════════════════════ */

function StatusActions({
  ticket,
  dict,
}: {
  ticket: TicketListItem;
  dict: TicketStatusDict;
}) {
  const dispatch = useAppDispatch();
  const hd = useHdPermissions();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Семантика статусов из справочника: «решить» доступно из любого
  // промежуточного статуса, «закрыть» — пока заявка не в финальном.
  const byCode = new Map(dict.statuses.map((s) => [s.code, s]));
  const cur = byCode.get(ticket.status);
  const isResolved = cur ? cur.is_resolved : ticket.status === 'solved';
  const isFinal = cur ? cur.is_final : ticket.status === 'closed';

  const canSolve = !isResolved && !isFinal;
  const canClose = !isFinal && hd.canReassignTicket;
  // Кастомные статусы из справочника — переход через PATCH; бэк
  // прогоняет смену через ту же бизнес-логику, что и solve/close.
  // Финальные кастомные гейтим тем же правом, что кнопку «Закрыть»
  // (бэк: закрытие доступно только админу/руководителю ТП).
  const customTargets = dict.statusesFor(ticket.ticket_type?.id).filter(
    (s) =>
      s.is_active &&
      !s.is_system &&
      s.code !== ticket.status &&
      (s.is_final ? hd.canReassignTicket : true),
  );
  if (!hd.canManageTickets || (!canSolve && !canClose && customTargets.length === 0)) {
    return null;
  }

  return (
    <div ref={ref} className="relative inline-block">
      <button
        className={`transition-opacity text-[#B0B3BE] hover:text-[#242426] ${
          open ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`}
        title="Изменить статус"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        <FiEdit className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 z-50 bg-white border border-[#ECEDF0] rounded-lg shadow-lg py-1 min-w-[140px]">
          {customTargets.map((s) => (
            <button
              key={s.code}
              onClick={(e) => {
                e.stopPropagation();
                dispatch(updateTicket({ id: ticket.id, data: { status: s.code } }));
                setOpen(false);
              }}
              className="w-full text-left px-3 py-1.5 text-xs text-[#242426] hover:bg-[#F4F4F6] flex items-center gap-2"
            >
              <span
                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: s.color }}
              />
              {s.name}
            </button>
          ))}
          {canSolve && (
            <button
              onClick={(e) => { e.stopPropagation(); dispatch(solveTicket(ticket.id)); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-[#242426] hover:bg-[#F4F4F6]"
            >
              Решить
            </button>
          )}
          {canClose && (
            <button
              onClick={(e) => { e.stopPropagation(); dispatch(closeTicket(ticket.id)); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-xs text-[#242426] hover:bg-[#F4F4F6]"
            >
              Закрыть
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════
   Опции для фильтров по колонкам
   ══════════════════════════════════════════════ */

// Статусные опции строятся динамически из справочника (см. useTicketStatusDict
// в компоненте); TICKET_STATUS_LABELS остаётся fallback'ом внутри словаря.
const priorityOptions = Object.entries(TICKET_PRIORITY_LABELS).map(([v, l]) => ({ value: v, label: l }));

/* ══════════════════════════════════════════════
   Основной компонент
   ══════════════════════════════════════════════ */

export default function TicketsTable({
  tickets,
  totalCount,
  currentPage,
  setCurrentPage,
  pageSize,
  isLoading,
  onColumnFilter,
}: TicketsTableProps) {
  const navigate = useNavigate();
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const statusDict = useTicketStatusDict();
  const statusOptions = statusDict.options;

  const handleFilter = (field: string, value: string) => {
    onColumnFilter?.(field, value);
  };

  /** Останавливает всплытие, чтобы клик по иконке не открывал детальную */
  const stopAndDo = (e: React.MouseEvent, fn: () => void) => {
    e.stopPropagation();
    fn();
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  /* Пагинация */
  const pages = (): (number | '...')[] => {
    const arr: (number | '...')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) arr.push(i);
    } else {
      arr.push(1);
      if (currentPage > 3) arr.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) arr.push(i);
      if (currentPage < totalPages - 2) arr.push('...');
      arr.push(totalPages);
    }
    return arr;
  };

  /* ── Состояния загрузки / пусто ── */
  if (isLoading) {
    return <div className="bg-white rounded-2xl p-12 text-center text-[#8B8E99] text-sm">Загрузка...</div>;
  }
  if (!tickets.length) {
    return <div className="bg-white rounded-2xl p-12 text-center text-[#8B8E99] text-sm">Заявок не найдено</div>;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* ── Таблица ── */}
      <div className="bg-white rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[#ECEDF0]">
              <ThFilter field="number" options={[]} onSelect={handleFilter}>№ Заявки</ThFilter>
              <Th>Тема</Th>
              <ThFilter field="company" options={[]} onSelect={handleFilter}>Клиент</ThFilter>
              <ThFilter field="assigned_to" options={[]} onSelect={handleFilter}>Комментарий</ThFilter>
              <ThFilter field="user" options={[]} onSelect={handleFilter}>Контакт</ThFilter>
              <Th>Дата регистрации</Th>
              <ThFilter field="status" options={statusOptions} onSelect={handleFilter}>Статус</ThFilter>
              <Th>SLA</Th>
            </tr>
          </thead>
          <tbody>
            {tickets.map((t) => (
              <tr
                key={t.id}
                onClick={() => navigate(`/helpdesk/${t.id}`)}
                className="border-b border-[#F4F4F6] hover:bg-[#FAFBFC] cursor-pointer transition-colors group"
              >
                {/* Номер */}
                <td className="py-3 px-4 text-[#242426] font-medium whitespace-nowrap">
                  {t.number}
                </td>

                {/* Тема */}
                <td className="py-3 px-4 text-[#242426] max-w-[280px]" title={t.subject}>
                  {cut(t.subject, 45)}
                </td>

                {/* Клиент (компания) */}
                <td className="py-3 px-4 text-[#242426] whitespace-nowrap">
                  {companyLabel(t.company)}
                </td>

                {/* Комментарий (контакт-лицо) */}
                <td className="py-3 px-4 text-[#242426] whitespace-nowrap">
                  {personName(t.user)}
                </td>

                {/* Контакт (ответственный) */}
                <td className="py-3 px-4 text-[#242426] whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <span>{personName(t.assigned_to)}</span>
                    {t.assigned_to && (
                      <button
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-[#B0B3BE] hover:text-[#242426]"
                        title="Копировать"
                        onClick={(e) =>
                          stopAndDo(e, () =>
                            copyToClipboard(
                              `${t.assigned_to?.first_name ?? ''} ${t.assigned_to?.last_name ?? ''}`.trim(),
                            ),
                          )
                        }
                      >
                        <FiCopy className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </td>

                {/* Дата */}
                <td className="py-3 px-4 text-[#6B6F7B] whitespace-nowrap">
                  {fmtDate(t.created_at)}
                </td>

                {/* Статус */}
                <td className="py-3 px-4 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <StatusBadge status={t.status} dict={statusDict} />
                    <StatusActions ticket={t} dict={statusDict} />
                  </div>
                </td>

                {/* SLA — компактный бейдж: «Ответ: осталось 2ч» / «✗ SLA».
                    На решённых тикетах показывает итоговую плашку. */}
                <td className="py-3 px-4 whitespace-nowrap">
                  <SlaIndicator sla={t.sla} ticketStatus={t.status} variant="compact" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ── Пагинация ── */}
      <div className="flex items-center justify-between pb-4">
        <div className="flex items-center gap-1">
          {pages().map((p, i) =>
            p === '...' ? (
              <span key={`d${i}`} className="px-2 py-1 text-[#8B8E99] text-sm">…</span>
            ) : (
              <button
                key={p}
                onClick={() => setCurrentPage(p)}
                className={`min-w-[32px] h-8 rounded-lg text-sm font-medium transition-colors ${
                  p === currentPage
                    ? 'bg-[#E53935] text-white'
                    : 'text-[#242426] hover:bg-[#F4F4F6]'
                }`}
              >
                {p}
              </button>
            ),
          )}
        </div>
        <div className="flex items-center gap-2 text-sm text-[#6B6F7B]">
          <span>Показать</span>
          <span className="px-3 py-1.5 border border-[#ECEDF0] rounded-lg bg-white font-medium text-[#242426]">
            {pageSize}
          </span>
          <span>на странице</span>
        </div>
      </div>
    </div>
  );
}
